import { Add, Delete, Edit, Inbox, RemoveRedEye, EventNote } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import DialogModal from "components/UI/DialogModal";
import { HeadCell } from "models/app";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IEmployee, IEmployeeInput, IEmployeeSheetInput } from "models/employee";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  EmployeeCells,
  EmployeeCellsExport,
  addEmployee,
  deleteEmployee,
  editEmployee,
  getEmployees,
  getRegistrationLink,
  getTemplateSheet,
  importEmployee,
  updateEmployee,
} from "framework/employee";

import Search from "components/Search/Search";
import AddEmployee from "./partials/AddEmployee/AddEmployee";
import EditEmployee from "./partials/EditEmployee/EditEmployee";
import DeleteEmployee from "./partials/DeleteEmployee/DeleteEmployee";
import { ICountry } from "models/country";
import { ICompanyJob } from "models/companyJob";
import { getCountries } from "framework/countries";
import { getCompanyJobs } from "framework/companyjobs";
import { handleError, isValidEmail } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { ICompanyDepartments } from "models/companyDepartment";
import { getCompanyDepartments } from "framework/companyDepartments";
import { IPermission } from "models/permissions";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "helpers/axiosInstance";
import ImportEmployee from "./partials/ImportEmployee/ImportEmployee";
import { UserRoleName } from "types/auth";
import { BASE_URL, GET_ENROLLMENTS_BY_EMPLOYEE, TEMPLATE_SHEET } from "constants/api";
import UpdateEmployees from "./partials/UpdateEmployees/updateEmployees";
import phone from 'phone';


interface Props {
  feature: Feature;
}

function isValidNumber(countryCodeNumeric: string): boolean {
  const { isValid } = phone(countryCodeNumeric);
  return isValid;

}

const Employees: React.FC<Props> = ({ feature }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [loading4, setLoading4] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [Employees, setEmployees] = useState<IEmployee[]>([] as IEmployee[]);
  const [FilterEmployees, setFilterEmployees] = useState<IEmployee[]>(
    [] as IEmployee[]
  );
  const [Countries, setCountries] = useState<ICountry[]>([] as ICountry[]);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [CompanyJobs, setJobs] = useState<ICompanyJob[]>([] as ICompanyJob[]);
  const [CompanyDepartments, setCompanyDepartments] = useState<
    ICompanyDepartments[]
  >([] as ICompanyDepartments[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openImport, setOpenImport] = useState<boolean>(false);
  const [openUpdateEmployees, setOpenUpdateEmployees] = useState<boolean>(false);
  const [openViewWorkshops, setOpenViewWorkshops] = useState<boolean>(false);
  const [importError, setImportError] = useState<string[] | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<IEmployee>(
    {} as IEmployee
  );
  const [importedEmployees, setImportedEmployees] = useState<IEmployee[]>(
    []
  );
  const [employeeWorkshops, setEmployeeWorkshops] = useState<any[]>([]);
  const [loadingWorkshops, setLoadingWorkshops] = useState<boolean>(false);
  const user = getUserData();
  
  // Workshop enrollment grid head cells
  const WorkshopEnrollmentCells: HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "T_name",
    },
    {
      id: "employeeId",
      numeric: false,
      disablePadding: false,
      label: "T_employee_id",
    },
    {
      id: "dateOfWorkshop",
      numeric: false,
      disablePadding: false,
      label: "T_date_of_workshop",
      isDate: true,
    },
    {
      id: "workshopName",
      numeric: false,
      disablePadding: false,
      label: "T_workshop_name",
    },
    {
      id: "preTestScore",
      numeric: true,
      disablePadding: false,
      label: "T_pre_test_score",
    },
    {
      id: "postTestScore",
      numeric: true,
      disablePadding: false,
      label: "T_post_test_score",
    },
    {
      id: "evaluationScore",
      numeric: true,
      disablePadding: false,
      label: "T_evaluation_score",
    },
    {
      id: "attendancePercentage",
      numeric: true,
      disablePadding: false,
      label: "T_attendance_percentage",
    },
    {
      id: "certificateLink",
      numeric: false,
      disablePadding: false,
      label: "T_certificate_link",
    },
  ];

  const WorkshopEnrollmentExportHeaders = [
    "name",
    "employeeId",
    "dateOfWorkshop",
    "workshopName",
    "preTestScore",
    "postTestScore",
    "evaluationScore",
    "attendancePercentage",
    "certificateLink",
  ];
  const { colors, roleName } = user;
  const [inputObj, setInputObj] = useState<IEmployeeInput>(
    {} as IEmployeeInput
  );

  const [inputObjSheet, setInputObjSheet] = useState<IEmployeeSheetInput>(
    {} as IEmployeeSheetInput
  );

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addEmployeesMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addEmployee(createInput);
    },
  });

  const editEmployeesMutation = useMutation({
    mutationFn: (data: { createInput: FormData; id: string }) => {
      return editEmployee(data.createInput, data.id);
    },
  });

  const deleteEmployeesMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteEmployee(code);
    },
  });


  const ImportEmployeesMutation = useMutation({
    mutationFn: (data: FormData) => {
      return importEmployee(data);
    },
  });

  const UpdateEmployeesMutation = useMutation({
    mutationFn: (data: any) => {
      return updateEmployee(data);
    },
  });

  const fetchCountries = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get Countries", () =>
        getCountries()
      );
      setCountries(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchCompanyDepartments = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery(
        "get Company Departments",
        () => getCompanyDepartments()
      );
      setCompanyDepartments(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchCompanyJobs = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get Jobs", () =>
        getCompanyJobs()
      );
      setJobs(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchData = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get Employees", () =>
        getEmployees()
      );

      setEmployees(
        response.map((item: IEmployee) => ({
          ...item,
          profilePicture: item.profilePicture,
        }))
      );
      setFilterEmployees(response);
      setLoading(false);
    } catch (error: any) {
      // setAlert({
      //   open: true,
      //   message: handleError(error) || "Something went wrong",
      //   type: "error",
      // });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    try {
      const res = await getPermission(15);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchRegistrationLink = useCallback(async () => {
    setLoading4(true);
    try {
      const res = await getRegistrationLink();
       if (res) {
      await navigator.clipboard.writeText(res); 

      setAlert({
        open: true,
        message: t('T_create_link_success'),
        type: "success",
      });
    }
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      
    } finally {setLoading4(false)}

    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  useEffect(() => {
    fetchCountries();
    fetchCompanyJobs();
    fetchCompanyDepartments();
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleChangeSheet = (name: string, value: any) => {
    setInputObjSheet((prev) => ({
      ...prev,
      [name]: value,
    }));
    setImportError(null)
  };


  const handleOpenAdd = () => setOpenAdd(true);

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setInputObj({} as IEmployeeInput);
  };

  const handleOpenEdit = (Employee: IEmployee) => {
    setSelectedEmployee(Employee);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedEmployee({} as IEmployee);
    setInputObj({} as IEmployeeInput);
  };

  const handleCloseUpdateEmployees = () => {
    setOpenUpdateEmployees(false);
  };

  // Format workshop enrollment data for display
  // Note: The GetEnrollmentsByEmployee API doesn't return preTestScore, postTestScore, 
  // evaluationScore, or attendancePercentage. These fields are set to null and will display as "-"
  // To populate these fields, additional API calls would be needed or the backend should be updated
  // to include this data in the GetEnrollmentsByEmployee response.
  const formatWorkshopEnrollmentData = (workshops: any[], employee: IEmployee) => {
    return workshops.map((workshop: any) => ({
      id: workshop.enrollmentId || workshop.id || Math.random().toString(),
      name: `${employee.firstName || ''} ${employee.middleName || ''} ${employee.lastName || ''}`.trim(),
      employeeId: employee.employeeId || '',
      dateOfWorkshop: workshop.fromDateDisplay || workshop.fromDate || workshop.dateOfWorkshop || '',
      workshopName: i18n.language === "ar" 
        ? (workshop.topicNameAr || workshop.topicName || '')
        : (workshop.topicName || workshop.topicNameAr || ''),
      // These fields are not returned by GetEnrollmentsByEmployee API
      // They would need to be fetched from separate endpoints or added to the backend response
      preTestScore: workshop.preTestScore || workshop.preTestGrade || workshop.preTest || null,
      postTestScore: workshop.postTestScore || workshop.postTestGrade || workshop.postTest || null,
      evaluationScore: workshop.evaluationScore || workshop.evaluation || workshop.evaluationGrade || null,
      attendancePercentage: workshop.attendancePercentage || workshop.attendance || null,
      certificateLink: '', // Empty as requested
      enrollmentId: workshop.enrollmentId,
      workshopId: workshop.workshopId,
    }));
  };

  const handleOpenViewWorkshops = async (employee: IEmployee) => {
    setSelectedEmployee(employee);
    setOpenViewWorkshops(true);
    setLoadingWorkshops(true);
    try {
      // Fetch employee workshops
      const response = await axiosInstance.get(`${GET_ENROLLMENTS_BY_EMPLOYEE}?employeeId=${employee.id}`);
      const workshops = response.data || [];
      // Format the data for display
      const formattedWorkshops = formatWorkshopEnrollmentData(workshops, employee);
      setEmployeeWorkshops(formattedWorkshops);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Error fetching employee workshops",
        type: "error",
      });
      setEmployeeWorkshops([]);
    } finally {
      setLoadingWorkshops(false);
    }
  };

  const handleCloseViewWorkshops = () => {
    setOpenViewWorkshops(false);
    setEmployeeWorkshops([]);
  };

  // Get body cells for workshop enrollment grid
  const getWorkshopEnrollmentBodyCells = (row: any) => {
    return WorkshopEnrollmentCells.map((headCell: HeadCell, i: number) => {
      let element: React.ReactNode;
      
      switch (headCell.id) {
        case "name":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.name}</Typography>
            </TableCell>
          );
          break;
        case "employeeId":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.employeeId}</Typography>
            </TableCell>
          );
          break;
        case "dateOfWorkshop":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.dateOfWorkshop || '-'}</Typography>
            </TableCell>
          );
          break;
        case "workshopName":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.workshopName || '-'}</Typography>
            </TableCell>
          );
          break;
        case "preTestScore":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.preTestScore !== null && row.preTestScore !== undefined ? row.preTestScore : '-'}</Typography>
            </TableCell>
          );
          break;
        case "postTestScore":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.postTestScore !== null && row.postTestScore !== undefined ? row.postTestScore : '-'}</Typography>
            </TableCell>
          );
          break;
        case "evaluationScore":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.evaluationScore !== null && row.evaluationScore !== undefined ? row.evaluationScore : '-'}</Typography>
            </TableCell>
          );
          break;
        case "attendancePercentage":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.attendancePercentage !== null && row.attendancePercentage !== undefined 
                  ? `${row.attendancePercentage}%` 
                  : '-'}
              </Typography>
            </TableCell>
          );
          break;
        case "certificateLink":
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2" component="a" href="#" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                {row.certificateLink || '-'}
              </Typography>
            </TableCell>
          );
          break;
        default:
          element = (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">-</Typography>
            </TableCell>
          );
      }
      
      return {
        id: headCell.id,
        element,
      };
    });
  };

  // Format value for Excel export
  const formatWorkshopExcelValue = (key: string, value: any, row: any) => {
    if (key === "attendancePercentage" && value !== null && value !== undefined) {
      return `${value}%`;
    }
    if (key === "certificateLink") {
      return value || '';
    }
    return value ?? '';
  };

  const handleOpenDelete = (Employee: IEmployee) => {
    setSelectedEmployee(Employee);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedEmployee({} as IEmployee);
  };

  const handleCloseImport = () => {
    setOpenImport(false);
    setImportError(null);
    setInputObjSheet({} as IEmployeeSheetInput);
  };

  const getBodyCells = (row: IEmployee) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "profilePicture")
        return {
          id: "ProfilePicture",
          element: (
            <TableCell align={"left"} key={i}>
              {row.profilePicture ? (
                <Box
                  component={"img"}
                  src={row?.profilePicture}
                  alt={row?.firstName}
                  width={50}
                  height={50}
                />
              ) : (
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {row?.firstName?.charAt(0)}
                </Avatar>
              )}
            </TableCell>
          ),
        };
      if (key === "firstName")
        return {
          id: "firstName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.firstName : row?.firstNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "middleName")
        return {
          id: "middleName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.middleName : row?.middleNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "lastName")
        return {
          id: "lastName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.lastName : row?.lastNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "email")
        return {
          id: "email",
          element: (
            <TableCell align={"left"} key={i}>
              <Tooltip title={row?.email}>
                <Typography variant="body2">{row?.email}</Typography>
              </Tooltip>
            </TableCell>
          ),
        };
      if (key === "phoneNumber")
        return {
          id: "phoneNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row?.countryCode}{row?.phoneNumber}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "employeeId")
        return {
          id: "employeeId",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.employeeId}</Typography>
            </TableCell>
          ),
        };
      if (key === "job")
        return {
          id: "job",
          element: (
            <TableCell align={"left"} key={i}>
              <Tooltip title={row?.job}>
                <Typography variant="body2">
                  {i18n.language === "en" ? row?.job : row?.jobAr}
                </Typography>
              </Tooltip>
            </TableCell>
          ),
        };
      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title={t("T_competencies")}>
                  <Link to={`/employeeCompetencies/${row.id}`}>
                    <IconButton size={"small"} color={"info"}>
                      <RemoveRedEye
                        sx={{
                          color: colors.viewIconColor
                            ? colors.viewIconColor
                            : "#0288d1",
                          "&:hover": { color: colors?.viewIconHoverColor },
                        }}
                      />
                    </IconButton>
                  </Link>
                </Tooltip>

                <Tooltip title={t("T_view_employee_workshops")}>
                  <IconButton 
                    size={"small"} 
                    sx={{
                      color: "#2e7d32",
                      "&:hover": { color: "#1b5e20" },
                    }}
                    onClick={() => handleOpenViewWorkshops(row)}
                  >
                    <EventNote />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.FirstName ||
      !inputObj.LastName ||
      !inputObj.CountryCode ||
      !inputObj.PhoneNumber ||
      !inputObj.Email ||
      !inputObj.ResidenceCountryId ||
      !inputObj.CompanyDepartmentId ||
      !inputObj.CompanyJobId

    )
      return setAlert({
        open: true,
        message: "Please fill all required fields",
        type: "error",
      });

    if (
      !isValidEmail(inputObj.Email)

    )
      return setAlert({
        open: true,
        message: "Please Enter Valid Email",
        type: "error",
      });

    if (
      !isValidNumber(inputObj.CountryCode + inputObj.PhoneNumber))


      return setAlert({
        open: true,
        message: "This Phone is not valid in this country",
        type: "error",
      });
    try {
      setLoading2(true);
      const createInput = new FormData();
      if(inputObj.EmployeeId) createInput.append("EmployeeId", inputObj.EmployeeId);
      createInput.append("FirstName", inputObj.FirstName);
      if(inputObj.MiddleName) createInput.append("MiddleName", inputObj.MiddleName);
      createInput.append("LastName", inputObj.LastName);
      createInput.append("FirstNameAr", inputObj.FirstNameAr);
      createInput.append("MiddleNameAr", inputObj.MiddleNameAr);
      createInput.append("LastNameAr", inputObj.LastNameAr);
      createInput.append("CountryCode", inputObj.CountryCode);
      createInput.append("Email", inputObj.Email);
      createInput.append("CompanyJobId", inputObj.CompanyJobId);
      createInput.append("ResidenceCountryId", inputObj.ResidenceCountryId);
      createInput.append("PhoneNumber", inputObj.PhoneNumber);
      if (inputObj.ProfilePicture)
        createInput.append("ProfilePicture", inputObj.ProfilePicture || inputObj.ProfilePicture?.[0]);
      await addEmployeesMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Employee added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    // if (
    //   !inputObj.FirstName &&
    //   !inputObj.LastName &&
    //   !inputObj.FirstNameAr &&
    //   !inputObj.MiddleNameAr &&
    //   !inputObj.LastNameAr &&
    //   !inputObj.PhoneNumber &&
    //   !inputObj.Email &&
    //   !inputObj.CompanyJobId &&
    //   !inputObj.CountryCode &&
    //   !inputObj.CompanyDepartmentId &&
    //   !inputObj.ResidenceCountryId &&
    //   !inputObj.EmployeeId &&
    //   !inputObj.ProfilePicture
    // )
    //   return setAlert({
    //     open: true,
    //     message: "No changes detected",
    //     type: "error",
    //   });
    if (inputObj.Email && !isValidEmail(inputObj.Email))
      return setAlert({
        open: true,
        message: "Please Enter Valid Email",
        type: "error",
      });

    if (
      !isValidNumber((inputObj.CountryCode || selectedEmployee.countryCode) + (inputObj.PhoneNumber || selectedEmployee.phoneNumber)))


      return setAlert({
        open: true,
        message: "This Phone is not valid in this country",
        type: "error",
      });

    try {
      setLoading2(true);
      const createInput = new FormData();
      if (inputObj.FirstName)
        createInput.append(
          "FirstName",
          inputObj.FirstName || selectedEmployee.firstName
        );
       createInput.append(
          "MiddleName",
          inputObj.MiddleName === null ? '' : inputObj?.MiddleName || selectedEmployee.middleName ||""
        );
      if (inputObj.LastName)
        createInput.append(
          "LastName",
          inputObj.LastName || selectedEmployee.lastName
        );
      if (inputObj.FirstNameAr)
        createInput.append(
          "FirstNameAr",
          inputObj.FirstNameAr || selectedEmployee.firstNameAr
        );
      if (inputObj.CountryCode)
        createInput.append(
          "CountryCode",
          inputObj.CountryCode || selectedEmployee?.countryCode
        );
        createInput.append(
          "MiddleNameAr",
          inputObj.MiddleNameAr === null ? '' : inputObj.MiddleNameAr || selectedEmployee.middleNameAr || ""
        );
      if (inputObj.LastNameAr)
        createInput.append(
          "LastNameAr",
          inputObj.LastNameAr || selectedEmployee.lastNameAr
        );
      if (inputObj.EmployeeId)
        createInput.append(
          "EmployeeId",
          inputObj.EmployeeId || selectedEmployee.employeeId
        );
      if (inputObj.ResidenceCountryId)
        createInput.append(
          "ResidenceCountryId",
          inputObj.ResidenceCountryId || selectedEmployee.residenceCountryId
        );
      if (inputObj.PhoneNumber)
        createInput.append(
          "PhoneNumber",
          inputObj.PhoneNumber || selectedEmployee.phoneNumber
        );
      if (inputObj.Email)
        createInput.append("Email", inputObj.Email || selectedEmployee.email);
      if (inputObj.CompanyJobId)
        createInput.append(
          "CompanyJobId",
          inputObj.CompanyJobId || selectedEmployee.companyJobId
        );
      if (inputObj.ProfilePicture)
        createInput.append("ProfilePicture", inputObj.ProfilePicture || inputObj.ProfilePicture?.[0]);
      await editEmployeesMutation.mutateAsync({
        createInput,
        id: selectedEmployee.id,
      });
      setAlert({
        open: true,
        message: "Employee updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleUpdate = async (data: any) => {
    try {
      setLoading2(true);
      await UpdateEmployeesMutation.mutateAsync(data);
      setAlert({
        open: true,
        message: "Employee updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseUpdateEmployees();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  }

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteEmployeesMutation.mutateAsync(selectedEmployee.id);
      setAlert({
        open: true,
        message: "Employee deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };


  const handleImport = async () => {
    try {
      setLoading2(true);

      const createInput = new FormData();
      if (inputObjSheet.EmployeeSheet)
        createInput.append("EmployeeSheet", inputObjSheet.EmployeeSheet || inputObjSheet.EmployeeSheet[0]);

      const res = await ImportEmployeesMutation.mutateAsync(createInput);
      setImportedEmployees(res?.employees)
      setAlert({
        open: true,
        message: "Employee imported successfully",
        type: "success",
      });
      fetchData();
      handleCloseImport();
      setOpenUpdateEmployees(true);
      setLoading2(false);
      setImportError(null)
    } catch (error: any) {
      setImportError(error.response.data.errors)
      // setAlert({
      //   open: true,
      //   message: handleError(error) || "Something went wrong",
      //   type: "error",
      // });
      setLoading2(false);
    }
  };


  const handleExport = async () => {
    try {
      setLoading2(true);

      // let response: any = await queryClient.fetchQuery("get template sheet", () =>
      //   getTemplateSheet()
      // );

      const response = await fetch(BASE_URL + TEMPLATE_SHEET, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'Authorization': 'Bearer ' + (localStorage.getItem('accessToken') || ''),
        },
      });


      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template.xlsx';
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);

      // setAlert({
      //   open: true,
      //   message: "Template Sheet Downloaded successfully",
      //   type: "success",
      // });
      fetchData();
      handleCloseImport();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };


  const handleSearch = async (value: string) => {
    if (!value) return setFilterEmployees(Employees);
    const search = Employees.filter((item) =>
      (item.firstName + " " + item.middleName + " " + item.lastName + " " + item.email + " " + item.job + " " + item.phoneNumber + " " + item.employeeId)
        .toLowerCase()
        .includes(value.toLowerCase())
    );
    setFilterEmployees(search);
  };


  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_employee")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>
        {roleName === UserRoleName.COMPANY_ADMIN && <Stack style={{ display: "flex", flexDirection: 'row', justifyContent: "end", alignItems: "center", gap: 10 }}>
          <Button variant="contained" disabled={loading4} color="success" style={{ borderRadius: 0 }} onClick={fetchRegistrationLink} >{t("T_create_link")}</Button>
          <Button variant="contained" style={{ borderRadius: 0 }} onClick={() => setOpenImport(true)}>{t("T_import")}</Button>
          <Button variant="outlined" onClick={handleExport}>{t("T_download_template")}</Button>
        </Stack>}

        {permission?.hasReadPermission! && FilterEmployees.length > 0 && (
          <List
            headCells={EmployeeCells}
            data={FilterEmployees}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
            //formatExcelValue={formatExcellValue}
            exportHeaders={EmployeeCellsExport}
          />
        )}
        {(!FilterEmployees || FilterEmployees.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_employee_header")}
              message={t("T_no_employee_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddEmployee
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          Countries={Countries}
          CompanyJobs={CompanyJobs}
          CompanyDepartments={CompanyDepartments}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditEmployee
          open={openEdit}
          loading={loading2}
          Employee={selectedEmployee}
          Countries={Countries}
          CompanyJobs={CompanyJobs}
          CompanyDepartments={CompanyDepartments}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteEmployee
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

      {openViewWorkshops && (
        <DialogModal
          open={openViewWorkshops}
          onClose={handleCloseViewWorkshops}
          title={`${t("T_employee_workshops")} - ${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
          maxWidth="xl"
          fullWidth
        >
          {loadingWorkshops ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : employeeWorkshops.length === 0 ? (
            <Typography variant="body1" color="textSecondary" align="center" p={3}>
              {t("T_no_workshops_found")}
            </Typography>
          ) : (
            <List
              headCells={WorkshopEnrollmentCells}
              data={employeeWorkshops}
              getBodyCells={getWorkshopEnrollmentBodyCells}
              canEdit={false}
              exportHeaders={WorkshopEnrollmentExportHeaders}
              formatExcelValue={formatWorkshopExcelValue}
            />
          )}
        </DialogModal>
      )}

      {openImport && (
        <ImportEmployee
          open={openImport}
          loading={loading2}
          error={importError}
          onChanges={handleChangeSheet}
          onClose={handleCloseImport}
          onSubmit={handleImport}
        />
      )}

      {openUpdateEmployees && (
        <UpdateEmployees
          open={openUpdateEmployees}
          loading={loading2}
          Countries={Countries}
          CompanyJobs={CompanyJobs}
          CompanyDepartments={CompanyDepartments}
          onClose={handleCloseUpdateEmployees}
          onSubmit={handleUpdate}
          feature={feature}
          importedEmployees={importedEmployees}
        />
      )}
    </>
  );
};

export default Employees;
